/**
 * SignUpAndInModal.js
 *
 * Parent modal that toggles between Sign In and Sign Up.
 * Uses subcomponents for cleaner separation and readability.
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useClientAuth } from "../../hooks/clientAuth";
import EmailSignUpForm from "./EmailSignUpForm";
import GoogleSignInButton from "./GoogleSignInButton";

const SignUpAndInModal = ({ isOpen, onClose }) => {
    const { signIn, signUp, signInWithGoogle } = useClientAuth();

    const [mode, setMode] = useState("signin"); // or 'signup'

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="
                            relative w-[90%] max-w-md bg-zinc-50
                            rounded-3xl shadow-2xl p-8 text-zinc-800
                            border border-zinc-200
                        "
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-600 transition"
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>

                        {/* Header */}
                        <h2 className="text-2xl font-semibold text-center mb-6 tracking-tight">
                            {mode === "signin" ? "Sign In" : "Create Account"}
                        </h2>

                        {/* --- Email & Password Form --- */}
                        <EmailSignUpForm
                            mode={mode}
                            signIn={signIn}
                            signUp={signUp}
                            onSuccess={onClose}
                        />

                        {/* Divider */}
                        <div className="flex items-center my-6">
                            <div className="flex-grow border-t border-zinc-300" />
                            <span className="mx-3 text-zinc-500 text-sm select-none">or</span>
                            <div className="flex-grow border-t border-zinc-300" />
                        </div>

                        {/* Google Sign-In */}
                        <GoogleSignInButton
                            signInWithGoogle={signInWithGoogle}
                            onSuccess={onClose}
                        />

                        {/* Mode Toggle */}
                        <p className="text-sm text-center mt-6 text-zinc-600">
                            {mode === "signin" ? (
                                <>
                                    Don’t have an account?{" "}
                                    <span
                                        onClick={() => setMode("signup")}
                                        className="text-zinc-800 font-medium hover:underline cursor-pointer"
                                    >
                                        Sign Up
                                    </span>
                                </>
                            ) : (
                                <>
                                    Already have an account?{" "}
                                    <span
                                        onClick={() => setMode("signin")}
                                        className="text-zinc-800 font-medium hover:underline cursor-pointer"
                                    >
                                        Sign In
                                    </span>
                                </>
                            )}
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SignUpAndInModal;